/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef} from 'react'

const capitalizeName = (name) => {
    if (!name) return ''
    return name.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
}
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'
import {
    AspectRatio,
    Button,
    Box,
    Divider,
    Link,
    Grid,
    Heading,
    Img,
    Image,
    Skeleton,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCustomerOrders, useProducts} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import PropTypes from 'prop-types'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {
    noOrdersTile,
    orderDetailLink,
    orderInfoContainerStyle,
    orderInfoListStyle,
    pageContainerStyle,
    productImageStyle
} from './order-history.style'
import {useToast} from '../../../../hooks/use-toast'
import {transformGiftCertificateItems} from '../../../../utills/transformBasket'
import {DEFAULT_ORDERS_SEARCH_PARAMS} from '../../../../constants'

const OrderGiftCardImages = ({giftCertificateItems}) => {
    const giftCards = transformGiftCertificateItems(giftCertificateItems)
    return giftCards?.map((giftCard, index) => {
        return (
            <AspectRatio
                key={index}
                ratio={1}
                width={{base: '82px', lg: '130px'}}
                borderRadius="base"
                overflow="hidden"
            >
                <Image
                    objectFit="contain !important"
                    alt={giftCard?.name}
                    src={giftCard?.imageUrl}
                    fallback={<Box background="gray.100" boxSize="full" />}
                />
            </AspectRatio>
        )
    })
}

const OrderProductImages = ({productItems}) => {
    const ids = productItems?.map((item) => item.productId).join(',') ?? ''
    const {data: {data: products} = {}, isLoading} = useProducts({
        parameters: {
            ids: ids
        }
    })

    const images = products?.map((product) => {
        // return product?.imageGroups?.find((group) => group.viewType === 'small')?.images[0]
        if (product?.imageGroups) {
            return product?.imageGroups?.find((group) => group.viewType === 'images')?.images[0]
        } else {
            return {
                alt: product?.name,
                link:
                    product?.c_cloudinary?.miniCartImage?.url || product?.c_cloudinary?.url || null,
                title: product?.name
            }
        }
    })

    return (
        <>
            {!isLoading && products
                ? images?.map((image, index) => {
                      return (
                          <AspectRatio
                              key={index}
                              ratio={1}
                              width={{base: '82px', lg: '130px'}}
                              borderRadius="base"
                              overflow="hidden"
                          >
                              <Img
                                  objectFit="contain !important"
                                  alt={image?.alt}
                                  src={image?.disBaseLink || image?.link}
                                  fallback={<Box background="gray.100" boxSize="full" />}
                              />
                          </AspectRatio>
                      )
                  })
                : productItems?.map((_, index) => {
                      return (
                          <Skeleton
                              key={index}
                              h={{base: '82px', lg: '130px'}}
                              w={{base: '82px', lg: '130px'}}
                          />
                      )
                  })}
        </>
    )
}
OrderProductImages.propTypes = {
    productItems: PropTypes.array
}

const onClient = typeof window !== 'undefined'
const AccountOrderHistory = () => {
    const showToast = useToast()
    const {formatDate, formatMessage} = useIntl()
    const navigate = useNavigation()

    const {data: customer} = useCurrentCustomer()
    const {customerId} = customer

    const searchParams = useSearchParams(DEFAULT_ORDERS_SEARCH_PARAMS)
    const {limit, offset} = searchParams[0]

    const {
        data: {data: orders} = {},
        isLoading,
        error: orderError
    } = useCustomerOrders(
        {
            parameters: {customerId, limit, offset}
        },
        {enabled: onClient && !!customerId}
    )

    const hasOrders = orders?.length > 0

    const headingRef = useRef()
    useEffect(() => {
        // Focus the 'Order History' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    const handleOnShopNowClick = () => {
        navigate('/')
    }

    if (orderError && !isLoading) {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
        return null
    }

    return (
        <Stack data-testid="account-order-history-page" sx={pageContainerStyle}>
            <Heading as="h1" fontSize="2xl" tabIndex="0" ref={headingRef}>
                <FormattedMessage defaultMessage="Orders" id="account_order_history.title.orders" />
            </Heading>
            {isLoading
                ? [1, 2, 3].map((i) => (
                      <Stack key={i} className="tile">
                          <Stack spacing={2}>
                              <Skeleton h="20px" w="112px" />
                              <Skeleton h="20px" w="200px" />
                          </Stack>
                          <Grid {...productImageStyle}>
                              {Array.from(Array(4).keys()).map((i) => (
                                  <Skeleton
                                      key={i}
                                      h={{base: '82px', lg: '130px'}}
                                      w={{base: '82px', lg: '130px'}}
                                  />
                              ))}
                          </Grid>
                          <Skeleton h="20px" w="200px" />
                      </Stack>
                  ))
                : orders?.map(
                      ({
                          creationDate,
                          orderNo,
                          productItems,
                          giftCertificateItems,
                          status,
                          externalOrderStatus,
                          currency,
                          orderTotal,
                          shipments
                      }) => (
                          <Stack key={orderNo} className="tile">
                              <Heading as="h2">
                                  <FormattedMessage
                                      defaultMessage="Ordered: {date}"
                                      id="account_order_history.label.ordered_date"
                                      values={{
                                          date: formatDate(new Date(creationDate), {
                                              year: 'numeric',
                                              day: 'numeric',
                                              month: 'numeric'
                                          })
                                      }}
                                  />
                              </Heading>
                              <Stack className="order-info">
                                  <Box>
                                      <Text as="span">
                                          <FormattedMessage
                                              defaultMessage="Order Number:"
                                              id="account_order_history.label.order_number_label"
                                          />{' '}
                                      </Text>
                                      <Text as="span">{orderNo}</Text>
                                  </Box>
                                  <Box>
                                      <Text as="span">
                                          <FormattedMessage
                                              defaultMessage="Order Status:"
                                              id="account_order_history.label.order_status_label"
                                          />{' '}
                                      </Text>
                                      <Text as="span">{externalOrderStatus || status}</Text>
                                  </Box>
                              </Stack>
                              <Grid {...productImageStyle}>
                                  {productImageStyle && (
                                      <OrderProductImages productItems={productItems} />
                                  )}
                                  {giftCertificateItems && (
                                      <OrderGiftCardImages
                                          giftCertificateItems={giftCertificateItems}
                                      />
                                  )}
                              </Grid>
                              <Stack {...orderInfoContainerStyle}>
                                  <Stack
                                      sx={orderInfoListStyle}
                                      divider={<Divider orientation="vertical" />}
                                  >
                                      <Text as="span">
                                          <FormattedMessage
                                              defaultMessage="{itemCount} {itemCount, plural, one {Item} other {Items}}"
                                              id="account_order_history.label.num_of_items_dynamic"
                                              description="Number of items in order"
                                              values={{
                                                  itemCount:
                                                      (productItems?.reduce(
                                                          (acc, {quantity}) => acc + quantity,
                                                          0
                                                      ) || 0) + (giftCertificateItems?.length || 0)
                                              }}
                                          />
                                      </Text>
                                      <Text as="span">
                                          <FormattedNumber
                                              style="currency"
                                              currency={currency}
                                              value={orderTotal}
                                          />
                                      </Text>
                                      <Text as="span">
                                          <FormattedMessage
                                              defaultMessage="Shipped to: {name}"
                                              id="account_order_history.label.shipped_to"
                                              values={{
                                                  name: `${capitalizeName(
                                                      shipments[0].shippingAddress.firstName
                                                  )} ${capitalizeName(
                                                      shipments[0].shippingAddress.lastName
                                                  )}`
                                              }}
                                          />
                                      </Text>
                                      <Link as={RouteLink} to="/content/returns-exchanges.html">
                                          <FormattedMessage
                                              defaultMessage="Return & Exchange Policy"
                                              id="account_order_history.label.return_exchange"
                                          />
                                      </Link>
                                  </Stack>
                                  <Link
                                      as={RouteLink}
                                      to={`/account/orders/${orderNo}`}
                                      {...orderDetailLink}
                                  >
                                      <FormattedMessage
                                          defaultMessage="View order Details"
                                          id="account_order_history.link.view_order_details"
                                      />
                                      {' >>'}
                                  </Link>
                              </Stack>
                          </Stack>
                      )
                  )}

            {!hasOrders && !isLoading && (
                <Stack
                    className="tile"
                    sx={noOrdersTile}
                    data-testid="account-order-history-place-holder"
                >
                    <Heading as="h2">
                        <FormattedMessage
                            defaultMessage="No Orders Placed Yet"
                            id="account_order_history.empty_history.no_orders_placed"
                        />
                    </Heading>
                    <Text>
                        <FormattedMessage
                            defaultMessage="It doesn't look like you've placed an online order yet. Come back here after you've placed your first order to see your order history."
                            id="account_order_history.empty_history.description"
                        />
                    </Text>
                    <Button
                        as={RouteLink}
                        variant="red"
                        className="shopNow-btn"
                        onClick={handleOnShopNowClick}
                    >
                        <FormattedMessage
                            defaultMessage="Shop Now"
                            id="account_order_history.empty_history.shop_now"
                        />
                    </Button>
                </Stack>
            )}
        </Stack>
    )
}

AccountOrderHistory.getTemplateName = () => 'account-order-history'

export default AccountOrderHistory
